import {
  Alert,
  Button,
  Container,
  Grid,
  Paper,
  Stack,
  Title,
  Text,
  Group,
  Badge,
  Loader,
  Center,
  NumberFormatter,
  SimpleGrid,
} from "@mantine/core";
import { Link, Redirect } from "wouter";
import { useStore } from "./store";
import { useEffect, useState } from "react";
import { getPimProductCount, getCreditUsageStats, getDiagnostics } from "./api";
import { IconCreditCard, IconDatabase, IconChartBar } from "@tabler/icons-react";
import { Trans, useTranslation } from "react-i18next";
import { usePlan } from "./hooks/usePlan";

function PlanNudge() {
  const { t } = useTranslation("common");
  const tenantProfile = useStore((state) => state.tenantProfile);
  const { isFree, productLimit } = usePlan();
  if (!tenantProfile) {
    return null;
  }

  const creditsLeft = tenantProfile?.creditTotals?.[0]?.creditsLeft ?? 0;

  return (
    <Grid.Col span={12}>
      {isFree && (
        <Alert
          title={t("dashboard.freePlanNudgeTitle")}
          mt="md"
          color="violet"
          variant="light"
        >
          <Trans
            t={t}
            i18nKey="dashboard.freePlanNudgeMessage"
            values={{ limit: productLimit.toLocaleString() }}
            components={{
              plansLink: <Link to="/plans" />,
              strong: <strong />,
            }}
          />
        </Alert>
      )}
      {isFree && creditsLeft === 0 && (
        <Alert title={t("dashboard.freePlanReached")} mt="md" color="red">
          <Trans
            t={t}
            i18nKey="dashboard.noCreditsMessage"
            components={{ creditsLink: <Link to="/credits" /> }}
          />
        </Alert>
      )}
      {!isFree && creditsLeft > 0 && creditsLeft < 1_000 && (
        <Alert title={t("dashboard.lowOnCredits")} mt="md" color="orange">
          <Trans
            t={t}
            i18nKey="dashboard.lowOnCreditsMessage"
            values={{ credits: creditsLeft.toLocaleString() }}
            components={{
              creditsLink: <Link to="/credits" />,
              plansLink: <Link to="/plans" />,
            }}
          />
        </Alert>
      )}
    </Grid.Col>
  );
}

function DashboardCard({ title, value, icon, loading, link }) {
  const { t } = useTranslation("common");
  return (
    <Paper withBorder p="md" radius="md">
      <Group justify="space-between">
        <div>
          <Text c="dimmed" tt="uppercase" fw={700} fz="xs">
            {title}
          </Text>
          <Text fw={700} fz="xl">
            {loading ? <Loader size="sm" /> : <NumberFormatter value={value} thousandSeparator />}
          </Text>
        </div>
        {icon}
      </Group>
      {link && (
        <Link to={link}>
          <Button variant="subtle" size="xs" mt="sm">
            {t("dashboard.viewDetails")}
          </Button>
        </Link>
      )}
    </Paper>
  );
}

function CreditUsageSection({ tenantProfile }) {
  const { t } = useTranslation("common");
  const [usageStats, setUsageStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getCreditUsageStats()
      .then((stats) => {
        setUsageStats(stats);
      })
      .catch((error) => {
        console.error("Failed to fetch credit usage stats:", error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  if (!tenantProfile?.creditTotals?.length) return null;

  const maxUsage = usageStats?.dailyUsage?.length > 0 
    ? Math.max(...usageStats.dailyUsage.map(d => d.creditsUsed), 1)
    : 1;

  return (
    <Paper withBorder p="md" radius="md">
      <Group justify="space-between" mb="md">
        <div>
          <Text fw={700} fz="lg">
            {t("dashboard.creditUsage")}
          </Text>
          <Text c="dimmed" fz="sm">
            {t("dashboard.dailyConsumption")}
          </Text>
        </div>
        <IconChartBar size={32} opacity={0.5} />
      </Group>
      
      {loading ? (
        <Center py="xl">
          <Loader />
        </Center>
      ) : usageStats?.dailyUsage?.length > 0 ? (
        <Stack gap="md">
          <Group justify="space-around">
            <div>
              <Text c="dimmed" fz="xs" tt="uppercase" fw={700}>
                {t("dashboard.totalUsed")}
              </Text>
              <Text fw={700} fz="xl">
                <NumberFormatter value={usageStats.totalUsed} thousandSeparator />
              </Text>
            </div>
            <div>
              <Text c="dimmed" fz="xs" tt="uppercase" fw={700}>
                {t("dashboard.totalAdded")}
              </Text>
              <Text fw={700} fz="xl" c="green">
                +<NumberFormatter value={usageStats.totalAdded} thousandSeparator />
              </Text>
            </div>
            <div>
              <Text c="dimmed" fz="xs" tt="uppercase" fw={700}>
                {t("dashboard.netChange")}
              </Text>
              <Text fw={700} fz="xl" c={usageStats.netChange >= 0 ? "green" : "red"}>
                {usageStats.netChange >= 0 ? "+" : ""}
                <NumberFormatter value={usageStats.netChange} thousandSeparator />
              </Text>
            </div>
          </Group>
          
          <div style={{ height: 200, display: "flex", alignItems: "flex-end", gap: 4 }}>
            {usageStats.dailyUsage.map((day, index) => {
              const height = (day.creditsUsed / maxUsage) * 100;
              const date = new Date(day.date);
              return (
                <div
                  key={index}
                  style={{
                    flex: 1,
                    height: `${Math.max(height, 2)}%`,
                    backgroundColor: "var(--mantine-color-blue-5)",
                    borderRadius: "4px 4px 0 0",
                    cursor: "pointer",
                    minWidth: 2,
                  }}
                  title={t("dashboard.dayUsageTooltip", {
                    date: date.toLocaleDateString(),
                    count: day.creditsUsed,
                  })}
                />
              );
            })}
          </div>
          <Text c="dimmed" fz="xs" ta="center">
            {t("dashboard.hoverBars")}
          </Text>
        </Stack>
      ) : (
        <Center py="xl">
          <Stack align="center">
            <Text c="dimmed" fz="sm">
              {t("dashboard.noCreditUsage")}
            </Text>
          </Stack>
        </Center>
      )}
    </Paper>
  );
}

export default function Home() {
  const { t } = useTranslation("common");
  const authToken = useStore((state) => state.authToken);
  const tenantProfile = useStore((state) => state.tenantProfile);
  // null until successfully loaded — the first-run redirect below must only
  // ever fire on a confirmed count of 0, never on a failed fetch.
  const [productCount, setProductCount] = useState(null);
  const [loadingProducts, setLoadingProducts] = useState(true);
  // null = unknown; only an explicit false (successful fetch, empty list)
  // participates in the first-run redirect.
  const [hasDiagnostics, setHasDiagnostics] = useState(null);

  useEffect(() => {
    if (authToken) {
      getPimProductCount()
        .then((count) => {
          setProductCount(count);
        })
        .catch((error) => {
          console.error("Failed to fetch product count:", error);
        })
        .finally(() => {
          setLoadingProducts(false);
        });
    }
  }, [authToken]);

  // First-run funnel: a fresh tenant (no products, no diagnostic reports)
  // lands on the free catalog diagnostic instead of an empty dashboard.
  useEffect(() => {
    if (authToken) {
      getDiagnostics()
        .then((list) => setHasDiagnostics((list?.length ?? 0) > 0))
        .catch(() => {
          // unknown — never redirect on error
        });
    }
  }, [authToken]);

  if (!authToken) {
    return <Redirect to="/auth/login" />;
  }

  if (productCount === 0 && hasDiagnostics === false) {
    return <Redirect to="/diagnose" />;
  }

  const creditsLeft = tenantProfile?.creditTotals?.[0]?.creditsLeft || 0;

  return (
    <Container p={0} size="lg">
      <Grid>
        <PlanNudge />
        
        {/* Dashboard Title */}
        <Grid.Col span={12}>
          <Title order={2} mb="md">{t("dashboard.title")}</Title>
        </Grid.Col>

        {/* 1. Credits Available */}
        <Grid.Col span={{ base: 12, md: 6 }}>
          <DashboardCard
            title={t("dashboard.creditsAvailable")}
            value={creditsLeft}
            icon={<IconCreditCard size={32} opacity={0.5} />}
            loading={!tenantProfile}
            link="/credits"
          />
        </Grid.Col>

        {/* 3. Total Products */}
        <Grid.Col span={{ base: 12, md: 6 }}>
          <DashboardCard
            title={t("dashboard.totalProducts")}
            value={productCount ?? 0}
            icon={<IconDatabase size={32} opacity={0.5} />}
            loading={loadingProducts}
            link="/pim/products"
          />
        </Grid.Col>

        {/* 2. Credit Usage History */}
        <Grid.Col span={12}>
          <CreditUsageSection tenantProfile={tenantProfile} />
        </Grid.Col>
      </Grid>
    </Container>
  );
}
